import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import { ChevronRight, PictureAsPdf, Print, Refresh } from "@mui/icons-material";
import { fetchManagementReport, fetchManagementReportDetails } from "../services/owdedApi";
import { dateOnly, money, number } from "./format";
import { EmptyState, Modal, Pagination } from "./ui";
import { paginateRows, PAGE_SIZE } from "./listUtils";

const today = () => new Date().toISOString().slice(0, 10);
const monthValue = () => new Date().toISOString().slice(0, 7);

function isoDate(date) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function rangeFromMode(mode, date, month, customRange) {
  if (mode === "DAY") return { start_date: date, end_date: date };
  if (mode === "WEEK") {
    const current = new Date(date);
    const day = current.getDay() || 7;
    const start = new Date(current);
    start.setDate(current.getDate() - day + 1);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start_date: isoDate(start), end_date: isoDate(end) };
  }
  if (mode === "MONTH") {
    const [year, monthIndex] = month.split("-").map(Number);
    return {
      start_date: isoDate(new Date(year, monthIndex - 1, 1)),
      end_date: isoDate(new Date(year, monthIndex, 0)),
    };
  }
  return customRange;
}

function ReportMatrix({ rows, currency, onOpen }) {
  const normalizedRows = rows.length % 2 === 0 ? rows : [...rows, { empty: true }];
  return (
    <div className="report-matrix">
      {normalizedRows.map((row, index) => row.empty ? (
        <div className="report-cell is-empty" key={`empty-${index}`} />
      ) : (
        <button className="report-cell is-clickable" type="button" key={`${row.label}-${index}`} onClick={() => onOpen(row)} title={`Voir le detail : ${row.label}`}>
          <span>{row.label}</span>
          <span className="report-cell__value">
            <strong>{row.type === "number" ? number(row.value) : money(row.value, currency)}</strong>
            <ChevronRight />
          </span>
        </button>
      ))}
    </div>
  );
}

function DetailTable({ detail, currency }) {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [detail]);
  const pagination = useMemo(() => paginateRows(detail?.rows || [], page, PAGE_SIZE), [detail, page]);

  if (!detail?.rows?.length) return <EmptyState label="Aucune operation ne compose cet indicateur." />;

  return (
    <>
      <div className="table-scroll report-detail-table">
        <table className="data-table">
          {detail.type === "investments" && <>
            <thead><tr><th>Dossier</th><th>Client</th><th>Produit</th><th>Date</th><th>Statut</th><th>Net investi</th><th>A recouvrer</th><th>Recouvre</th><th>Reste</th></tr></thead>
            <tbody>{pagination.rows.map((row) => <tr key={row.id}><td>{row.collection_card_id || row.reference}</td><td>{row.client_name}<br /><small>{row.client_number}</small></td><td>{row.product_name}</td><td>{dateOnly(row.investment_date || row.created_at)}</td><td>{row.status}</td><td>{money(row.net_disbursement_amount, row.currency || currency)}</td><td>{money(row.amount_to_recover, row.currency || currency)}</td><td>{money(row.amount_recovered, row.currency || currency)}</td><td>{money(row.remaining_amount, row.currency || currency)}</td></tr>)}</tbody>
          </>}
          {detail.type === "recoveries" && <>
            <thead><tr><th>Date</th><th>Dossier</th><th>Client</th><th>Produit</th><th>Agent</th><th>Observation</th><th>Montant</th></tr></thead>
            <tbody>{pagination.rows.map((row) => <tr key={row.id}><td>{dateOnly(row.paid_at)}</td><td>{row.investment_reference}</td><td>{row.client_name}</td><td>{row.product_name}</td><td>{row.agent_name || "-"}</td><td>{row.observation || "-"}</td><td>{money(row.amount_paid, row.currency || currency)}</td></tr>)}</tbody>
          </>}
          {detail.type === "clients" && <>
            <thead><tr><th>Numero</th><th>Client</th><th>Telephone</th><th>Activite</th><th>Statut</th><th>Risque</th><th>Creation</th></tr></thead>
            <tbody>{pagination.rows.map((row) => <tr key={row.id}><td>{row.client_number}</td><td>{row.full_name}</td><td>{row.phone || "-"}</td><td>{row.main_activity || "-"}</td><td>{row.client_status}</td><td>{row.risk_score || 0}/10</td><td>{dateOnly(row.created_at)}</td></tr>)}</tbody>
          </>}
          {detail.type === "accounts" && <>
            <thead><tr><th>Compte</th><th>Type</th><th>Banque</th><th>Numero</th><th>Devise</th><th>Solde</th><th>Statut</th></tr></thead>
            <tbody>{pagination.rows.map((row) => <tr key={row.id}><td>{row.name}</td><td>{row.account_type === "BANK" ? "Banque" : "Caisse"}</td><td>{row.bank_name || "-"}</td><td>{row.account_number || "-"}</td><td>{row.currency}</td><td>{money(row.current_balance, row.currency || currency)}</td><td>{row.status}</td></tr>)}</tbody>
          </>}
        </table>
      </div>
      <Pagination page={pagination.safePage} totalPages={pagination.totalPages} totalItems={detail.rows.length} from={pagination.from} to={pagination.to} onPageChange={setPage} />
    </>
  );
}

function ReportsPage() {
  const [report, setReport] = useState(null);
  const [currency, setCurrency] = useState("CDF");
  const [mode, setMode] = useState("DAY");
  const [date, setDate] = useState(today());
  const [month, setMonth] = useState(monthValue());
  const [period, setPeriod] = useState({ start_date: today(), end_date: today() });
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const reportRef = useRef(null);

  const activeRange = useMemo(() => rangeFromMode(mode, date, month, period), [date, mode, month, period]);
  const scopeLabel = mode === "DAY" ? "DU JOUR" : "DE LA PERIODE";
  const scopeLabelLower = mode === "DAY" ? "du jour" : "de la periode";
  const periodLabel = useMemo(() => {
    const start = report?.start || activeRange.start_date;
    const end = report?.end || activeRange.end_date;
    if (mode === "DAY") return start;
    if (mode === "MONTH") return `Mois du ${start} au ${end}`;
    if (mode === "WEEK") return `Semaine du ${start} au ${end}`;
    return `Periode du ${start} au ${end}`;
  }, [activeRange.end_date, activeRange.start_date, mode, report?.end, report?.start]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await fetchManagementReport({ ...activeRange, currency }));
      setNotice("");
    } catch (err) {
      setNotice(err.response?.data?.error || "Impossible de charger le rapport.");
    } finally {
      setLoading(false);
    }
  }, [activeRange, currency]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setDetail({ title: row.label, type: "investments", rows: [] });
    setDetailLoading(true);
    try {
      setDetail(await fetchManagementReportDetails({ ...activeRange, currency, metric: row.metric }));
    } catch (err) {
      setNotice(err.response?.data?.error || "Impossible de charger le detail du rapport.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const financial = report?.recap_general?.financial || {};
  const portfolio = report?.recap_general?.portfolio || {};
  const recovery = report?.activities?.recovery || {};
  const investments = report?.activities?.investments || {};
  const recap = report?.period_recap || {};

  const financialRows = [
    { label: "Montant total investi", value: financial.total_invested, metric: "total_invested" },
    { label: "Montant non investi", value: financial.non_invested, metric: "non_invested" },
    { label: "Total a recouvrer", value: financial.total_to_recover, metric: "total_to_recover" },
    { label: "Montant deja recouvre", value: financial.already_recovered, metric: "already_recovered" },
    { label: "Solde caisse", value: financial.cash_balance, metric: "cash_balance" },
    { label: "Restant a recouvrer", value: financial.remaining_to_recover, metric: "remaining_to_recover" },
    { label: "Solde banque", value: financial.bank_balance, metric: "bank_balance" },
  ];
  const portfolioRows = [
    { label: "N/Total clients", value: portfolio.total_clients, type: "number", metric: "total_clients" },
    { label: "Nouveau client", value: portfolio.new_clients, type: "number", metric: "new_clients" },
    { label: "N/Client servi", value: portfolio.served_clients, type: "number", metric: "served_clients" },
    { label: "N/Client non servi", value: portfolio.unserved_clients, type: "number", metric: "unserved_clients" },
  ];
  const recoveryRows = [
    { label: "Recouvrement/CS", value: recovery.recovery_cs, metric: "recovery_cs" },
    { label: "Recouvrement/CA", value: recovery.recovery_ca, metric: "recovery_ca" },
    { label: "Paiement dette", value: recovery.debt_payment, metric: "debt_payment" },
    { label: "Total encaisse", value: recovery.total_collected, metric: "total_collected" },
  ];
  const investmentRows = [
    { label: "N/Client validé", value: investments.clients_to_serve, type: "number", metric: "clients_to_serve" },
    { label: "N/Client servi", value: investments.clients_served, type: "number", metric: "clients_served_period" },
    { label: "N/Client en attente de validation", value: investments.clients_unserved, type: "number", metric: "clients_unserved_period" },
    { label: "Montant investi A.", value: investments.invested_actual, metric: "invested_actual" },
    { label: "Montant a investir P.", value: investments.investment_pending, metric: "investment_pending" },
  ];
  const recapRows = [
    { label: "Encaisse A.", value: recap.collected_actual, metric: "total_collected" },
    { label: "Montant total investi", value: recap.total_invested, metric: "invested_actual" },
    { label: "Montant deja recouvre", value: recap.already_recovered, metric: "period_recovered" },
    { label: "Montant non investi", value: recap.non_invested, metric: "investment_pending" },
    { label: "Restant a recouvrer", value: recap.remaining_to_recover, metric: "period_remaining_to_recover" },
    { label: "Total a recouvrer", value: recap.total_to_recover, metric: "period_total_to_recover" },
    { label: "Solde caisse", value: recap.cash_balance, metric: "cash_balance" },
    { label: "Solde banque", value: recap.bank_balance, metric: "bank_balance" },
  ];

  const cleanPdfText = (value) => String(value ?? "-").replace(/[\u202f\u00a0]/g, " ");
  const pdfMoney = (value) => cleanPdfText(money(value, currency));

  const buildPdf = () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const margin = 12;
    const width = 210 - margin * 2;
    let y = 12;
    const ensureSpace = (height) => {
      if (y + height <= 285) return;
      pdf.addPage();
      y = 12;
    };
    const header = () => {
      pdf.setFillColor(6, 37, 81);
      pdf.roundedRect(margin, y, width, 27, 2, 2, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(15);
      pdf.text("OWSUR - RAPPORT DE GESTION", margin + 6, y + 9);
      pdf.setFontSize(10);
      pdf.text("Situation des investissements et du recouvrement", margin + 6, y + 16);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.5);
      pdf.text(`Date : ${cleanPdfText(periodLabel)}   |   Devise : ${currency}`, margin + 6, y + 22);
      y += 33;
    };
    const section = (title) => {
      ensureSpace(14);
      pdf.setFillColor(6, 37, 81);
      pdf.rect(margin, y, width, 9, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9.5);
      pdf.text(cleanPdfText(title), margin + 4, y + 6);
      y += 12;
    };
    const subsection = (title) => {
      ensureSpace(9);
      pdf.setTextColor(15, 42, 68);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.text(cleanPdfText(title), margin + 2, y + 5);
      y += 8;
    };
    const matrix = (rows) => {
      const normalized = rows.length % 2 === 0 ? rows : [...rows, { empty: true }];
      const cellWidth = width / 2;
      for (let index = 0; index < normalized.length; index += 2) {
        ensureSpace(15);
        for (let column = 0; column < 2; column += 1) {
          const row = normalized[index + column];
          const x = margin + column * cellWidth;
          pdf.setDrawColor(216, 226, 239);
          pdf.setFillColor(row?.empty ? 247 : 255, row?.empty ? 251 : 255, row?.empty ? 255 : 255);
          pdf.rect(x, y, cellWidth, 14, "FD");
          if (!row?.empty) {
            pdf.setTextColor(82, 96, 113);
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(7.5);
            pdf.text(cleanPdfText(row.label), x + 3, y + 5);
            pdf.setTextColor(6, 29, 59);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(8.5);
            const value = row.type === "number" ? cleanPdfText(number(row.value)) : pdfMoney(row.value);
            pdf.text(value, x + cellWidth - 3, y + 10.5, { align: "right" });
          }
        }
        y += 14;
      }
      y += 4;
    };

    header();
    section("I. RECAP GENERAL");
    subsection("A. Situation financiere");
    matrix(financialRows);
    subsection("B. Situation du portefeuille clients");
    matrix(portfolioRows);
    section(`II. SITUATION DES ACTIVITES ${scopeLabel}`);
    subsection(`A. Recouvrement ${scopeLabelLower}`);
    matrix(recoveryRows);
    subsection("B. Investissements accordes");
    matrix(investmentRows);
    section(`III. RECAP ${scopeLabel}`);
    matrix(recapRows);
    section("IV. OBSERVATIONS ET RECOMMANDATIONS");
    pdf.setTextColor(51, 72, 102);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    (report?.observations || ["Aucune observation disponible."]).forEach((item) => {
      const lines = pdf.splitTextToSize(`- ${cleanPdfText(item)}`, width - 6);
      ensureSpace(lines.length * 4 + 2);
      pdf.text(lines, margin + 3, y + 4);
      y += lines.length * 4 + 2;
    });
    return pdf;
  };

  const previewPdf = () => window.open(buildPdf().output("bloburl"), "_blank");
  const printPdf = () => {
    const pdf = buildPdf();
    pdf.autoPrint();
    window.open(pdf.output("bloburl"), "_blank");
  };

  return (
    <main className="page-content management-page">
      <div className="page-head"><div><h1>Rapport de gestion</h1><p>Recapitulatif financier, portefeuille, activites et recommandations.</p></div><div className="table-actions"><button className="ghost-btn" type="button" onClick={previewPdf}><PictureAsPdf /> Visionner PDF</button><button className="primary-btn" type="button" onClick={printPdf}><Print /> Imprimer</button></div></div>
      {notice && <p className="notice">{notice}</p>}
      <section className="panel form-panel"><div className="table-toolbar"><select value={currency} onChange={(event) => setCurrency(event.target.value)}><option value="CDF">CDF</option><option value="USD">USD</option></select><select value={mode} onChange={(event) => setMode(event.target.value)}><option value="DAY">Jour</option><option value="WEEK">Hebdomadaire</option><option value="MONTH">Mois</option><option value="PERIOD">Periode</option></select>{mode === "MONTH" ? <input type="month" value={month} onChange={(event) => setMonth(event.target.value)} /> : mode === "PERIOD" ? <><input type="date" value={period.start_date} onChange={(event) => setPeriod((current) => ({ ...current, start_date: event.target.value }))} /><input type="date" value={period.end_date} onChange={(event) => setPeriod((current) => ({ ...current, end_date: event.target.value }))} /></> : <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />}<button className="primary-btn" type="button" onClick={load} disabled={loading}><Refresh /> {loading ? "Chargement..." : "Actualiser"}</button></div></section>
          <br/>
      <div className="report-print-surface structured-report" ref={reportRef}>
        <header className="report-document-head"><div className="report-brand-mark"><span>OWSUR</span></div><div><span>Rapport de gestion</span><h2>Situation des investissements et du recouvrement</h2><p>Date : {periodLabel}</p></div><div className="report-document-meta"><span>Devise</span><strong>{currency}</strong></div></header>
        <section className="panel report-section"><h2>I. RECAP GENERAL</h2><div className="report-subsection"><h3>A. Situation financiere</h3><ReportMatrix rows={financialRows} currency={currency} onOpen={openDetail} /></div><div className="report-subsection"><h3>B. Situation du portefeuille clients</h3><ReportMatrix rows={portfolioRows} currency={currency} onOpen={openDetail} /></div></section>
        <section className="panel report-section"><h2>II. SITUATION DES ACTIVITES {scopeLabel}</h2><div className="report-subsection"><h3>A. Recouvrement {scopeLabelLower}</h3><ReportMatrix rows={recoveryRows} currency={currency} onOpen={openDetail} /></div><div className="report-subsection"><h3>B. Investissements accordes</h3><ReportMatrix rows={investmentRows} currency={currency} onOpen={openDetail} /></div></section>
        <section className="panel report-section"><h2>III. RECAP {scopeLabel}</h2><ReportMatrix rows={recapRows} currency={currency} onOpen={openDetail} /></section>
        <section className="panel report-section"><h2>IV. OBSERVATIONS ET RECOMMANDATIONS</h2><ul className="observations-list">{(report?.observations || ["Aucune observation disponible."]).map((item) => <li key={item}>{item}</li>)}</ul></section>
      </div>
      {detail && <Modal title={detail.title || "Detail du rapport"} onClose={() => setDetail(null)}>{detailLoading ? <EmptyState label="Chargement du detail..." /> : <DetailTable detail={detail} currency={currency} />}</Modal>}
    </main>
  );
}

export default ReportsPage;
